// components/Layout.jsx
import { useState, useEffect } from "react";
import Sidebar from "./CustomNavBar"; // your Sidebar component
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton, CircularProgress } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useAuth } from "../../contexts/AuthContext";
import { authApi } from "../../api/authApi";
import { useBranding } from "../../hooks/useBranding";
import { ALLOWED_ROUTES_BY_ROLE } from "../../config/menuConfig";


const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user_type, isLoading, isAuthenticated, logout } = useAuth();
  const { handleContextLogout } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();

  const role = user_type?.toLowerCase();
  const allowedRoutes = ALLOWED_ROUTES_BY_ROLE[role] || [];
  const normalizedPath = location.pathname.toLowerCase().replace(/\/$/, "");

  const allLayoutRoutes = Object.values(ALLOWED_ROUTES_BY_ROLE).flat();
  const isLayoutRoute = allLayoutRoutes.some(r => r.toLowerCase().replace(/\/$/, "") === normalizedPath);
  const hasAccess = !isLayoutRoute || allowedRoutes.some(r => r.toLowerCase().replace(/\/$/, "") === normalizedPath);

  // Guard against browser Back/Forward Cache (bfcache):
  // When the browser restores a frozen page via back/forward navigation,
  // React doesn't re-mount and stale auth state (isAuthenticated=true) persists.
  // Forcing a reload ensures AuthContext.rehydrate() runs again and checks the
  // logged_out cookie, which blocks re-authentication after logout.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Case 1: Wrong role trying to access another role's route → logout + redirect
  useEffect(() => {
    if (!isLoading && isAuthenticated && !hasAccess) {
      const performUnauthorizedLogout = async () => {
        try {
          await authApi.logout();
        } catch (error) {
          // console.error("Logout failed during unauthorized access:", error);
        } finally {
          handleContextLogout();
          logout();
          navigate("/email/redirect", { replace: true });
        }
      };
      performUnauthorizedLogout();
    }
  }, [isLoading, isAuthenticated, hasAccess, navigate, handleContextLogout, logout]);

  // Case 2: Not authenticated (e.g. back button after logout) → call logout API + redirect
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const performBackButtonLogout = async () => {
        try {
          await authApi.logout();
        } catch (error) {
          // console.error("Logout API call failed on back-button:", error);
        } finally {
          handleContextLogout();
          navigate("/email/redirect", { replace: true });
        }
      };
      performBackButtonLogout();
    }
  }, [isLoading, isAuthenticated, navigate, handleContextLogout]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // While the session is being rehydrated, show a spinner
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated after loading (e.g. back button after logout) → spinner while useEffect redirects
  if (!isAuthenticated) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // If user has no access to this route, show spinner while useEffect triggers logout+redirect
  if (!user_type || !hasAccess) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Sidebar */}
      <Sidebar
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        userType={user_type}
        isAuthenticated={isAuthenticated}
      />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflowY: "auto",
        }}
      >
        {/* Mobile toggle button */}
        <IconButton
          color="inherit"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ display: { sm: "none" }, mb: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
